import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const scan = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const WidgetsContainer = styled.div`
  position: absolute;
  top: 60px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 50;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Widget = styled.div`
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid rgba(0, 255, 0, 0.4);
  border-radius: 12px;
  padding: 18px;
  backdrop-filter: blur(15px);
  min-width: 280px;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  box-shadow: 0 8px 32px rgba(0, 255, 0, 0.1);
`;

const WidgetTitle = styled.div`
  color: #00ffff;
  font-weight: bold;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
`;

const SecurityStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatusItem = styled.div<{ status: 'safe' | 'warning' | 'danger' }>`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 255, 0, 0.1);
  color: ${props => 
    props.status === 'safe' ? '#00ff00' :
    props.status === 'warning' ? '#ffaa00' : '#ff4444'
  };
`;

const NetworkMonitor = styled.div`
  position: relative;
  height: 80px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 6px;
  overflow: hidden;
  margin-top: 12px;
  border: 1px solid rgba(0, 255, 0, 0.2);
`;

const ScanLine = styled.div`
  position: absolute;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent, #00ff00, transparent);
  animation: ${scan} 2s linear infinite;
  box-shadow: 0 0 10px #00ff00;
`;

const NetworkData = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  font-size: 11px;
`;

const TerminalPreview = styled.div`
  background: rgba(0, 0, 0, 0.9);
  border-radius: 6px;
  padding: 12px;
  font-size: 10px;
  height: 100px;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(0, 255, 0, 0.2);
`;

const TerminalLine = styled.div<{ delay: number }>`
  opacity: 0;
  animation: ${pulse} 0.5s ease-in-out ${props => props.delay}s forwards;
  margin-bottom: 2px;
`;

const CTFStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CTFRank = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const RankBadge = styled.span<{ rank: number }>`
  background: ${props => 
    props.rank <= 3 ? '#ffd700' :
    props.rank <= 10 ? '#c0c0c0' : '#cd7f32'
  };
  color: #000;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const SkillMeter = styled.div`
  margin-top: 12px;
`;

const SkillBar = styled.div<{ level: number }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 6px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid rgba(0, 255, 0, 0.2);
`;

const Progress = styled.div<{ width: number }>`
  height: 100%;
  width: ${props => props.width}%;
  background: linear-gradient(90deg, #ff0000, #ffff00, #00ff00);
  transition: width 2s ease-in-out;
  box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
`;

const SystemStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
`;

const StatBox = styled.div`
  background: rgba(0, 0, 0, 0.6);
  padding: 8px;
  border-radius: 6px;
  text-align: center;
  border: 1px solid rgba(0, 255, 0, 0.2);
`;

const StatValue = styled.div`
  font-size: 16px;
  color: #00ffff;
  font-weight: bold;
`;

const StatLabel = styled.div`
  font-size: 9px;
  color: #cccccc;
  margin-top: 2px;
`;

const DesktopWidgets: React.FC = () => {
  const [securityScans, setSecurityScans] = useState(0);
  const [networkActivity, setNetworkActivity] = useState({ in: 0, out: 0 });
  const [terminalLines, setTerminalLines] = useState<string[]>([]);
  const [uptime, setUptime] = useState(0);

  useEffect(() => {
    // Security scan counter
    const scanTimer = setInterval(() => {
      setSecurityScans(prev => prev + Math.floor(Math.random() * 3) + 1);
    }, 3000);

    // Network activity simulation
    const networkTimer = setInterval(() => {
      setNetworkActivity({
        in: Math.floor(Math.random() * 1000),
        out: Math.floor(Math.random() * 500)
      });
    }, 2000);

    // Uptime counter
    const uptimeTimer = setInterval(() => {
      setUptime(prev => prev + 1);
    }, 1000);

    // Terminal activity simulation
    const terminalCommands = [
      'nmap -sS 192.168.1.0/24',
      'hydra -l admin -P passwords.txt ssh://target',
      'sqlmap -u "http://target.com" --dbs',
      'john --wordlist=rockyou.txt hashes.txt',
      'metasploit > use exploit/multi/handler',
      'burpsuite --target=https://example.com',
      'nikto -h https://vulnerable-site.com',
      'gobuster dir -u http://target -w wordlist.txt'
    ];

    const terminalTimer = setInterval(() => {
      const randomCommand = terminalCommands[Math.floor(Math.random() * terminalCommands.length)];
      setTerminalLines(prev => {
        const newLines = [...prev, `$ ${randomCommand}`];
        return newLines.slice(-5); // Keep only last 5 lines
      });
    }, 4000);

    return () => {
      clearInterval(scanTimer);
      clearInterval(networkTimer);
      clearInterval(terminalTimer);
      clearInterval(uptimeTimer);
    };
  }, []);

  const skills = [
    { name: 'Python', level: 90 },
    { name: 'Pentesting', level: 85 },
    { name: 'Cryptography', level: 75 },
    { name: 'Forensics', level: 80 },
    { name: 'Web Security', level: 88 }
  ];

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <WidgetsContainer>
      {/* Security Status Widget */}
      <Widget>
        <WidgetTitle>
          🛡️ Security Dashboard
        </WidgetTitle>
        <SecurityStatus>
          <StatusItem status="safe">
            <span>Firewall Status</span>
            <span>ACTIVE</span>
          </StatusItem>
          <StatusItem status="safe">
            <span>Antivirus Engine</span>
            <span>UPDATED</span>
          </StatusItem>
          <StatusItem status="warning">
            <span>VPN Connection</span>
            <span>DISCONNECTED</span>
          </StatusItem>
          <StatusItem status="safe">
            <span>Security Scans</span>
            <span>{securityScans}</span>
          </StatusItem>
        </SecurityStatus>
      </Widget>

      {/* Network Monitor Widget */}
      <Widget>
        <WidgetTitle>
          📡 Network Activity
        </WidgetTitle>
        <NetworkMonitor>
          <ScanLine />
          <NetworkData>
            <span>↓ {networkActivity.in} KB/s</span>
            <span>↑ {networkActivity.out} KB/s</span>
          </NetworkData>
        </NetworkMonitor>
        <SystemStats>
          <StatBox>
            <StatValue>{formatUptime(uptime)}</StatValue>
            <StatLabel>UPTIME</StatLabel>
          </StatBox>
          <StatBox>
            <StatValue>127</StatValue>
            <StatLabel>PROCESSES</StatLabel>
          </StatBox>
        </SystemStats>
      </Widget>

      {/* Live Terminal Widget */}
      <Widget>
        <WidgetTitle>
          💻 Active Terminal
        </WidgetTitle>
        <TerminalPreview>
          {terminalLines.map((line, index) => (
            <TerminalLine key={index} delay={index * 0.2}>
              {line}
            </TerminalLine>
          ))}
        </TerminalPreview>
      </Widget>

      {/* CTF Rankings Widget */}
      <Widget>
        <WidgetTitle>
          🏆 CTF Achievements
        </WidgetTitle>
        <CTFStatus>
          <CTFRank>
            <span>H7CTF Global Rank</span>
            <RankBadge rank={2}>#2</RankBadge>
          </CTFRank>
          <CTFRank>
            <span>TryHackMe Status</span>
            <span style={{ color: '#00ff00' }}>Top 1%</span>
          </CTFRank>
          <CTFRank>
            <span>Active Challenges</span>
            <span style={{ color: '#ffaa00' }}>3</span>
          </CTFRank>
          <CTFRank>
            <span>Points Earned</span>
            <span style={{ color: '#00ffff' }}>15,847</span>
          </CTFRank>
        </CTFStatus>
      </Widget>

      {/* Skills Monitor Widget */}
    
    </WidgetsContainer>
  );
};

export default DesktopWidgets;
